/*
 * fleet-model — pure view-model helpers over FleetGateway[]. No Lit, no DOM: stats,
 * the Status/Region/Channel grouping, geo→lat/lng for the map, and display formatting.
 *
 * DATA REALITY: the FleetControl proto carries status, org/claim, version, hostname,
 * geo, the network legs, and first/last-seen — those are REAL here. Telemetry
 * (CPU/MEM/DISK/TEMP), controller counts, symbol counts, uptime, MAC, and precise
 * lat/lng are NOT in the proto yet, so anything that needs them renders DASH or an
 * approximate (flagged) position until that data lands. See the cards/panel.
 */
import type { BadgeColor } from '@vitesse/base-ui';
import type { FleetGateway } from './fleet-client';
import { ft } from './i18n';

/** The "no data yet" placeholder — one spelling everywhere. */
export const DASH = '————';

/** Capitalize the first letter (connection/node-kind labels read "Edge", not "edge"). */
export const cap = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// ── status → badge color/label (fleet console: offline is notable → red) ──
export function statusColor(status: FleetGateway['status']): BadgeColor {
  return status === 'online' ? 'green' : status === 'offline' ? 'red' : 'gray';
}
export function statusLabel(status: FleetGateway['status']): string {
  return status === 'online' ? ft('status.online') : status === 'offline' ? ft('status.offline') : ft('status.unknown');
}

/** How old a health snapshot may be and still be shown as a reading.
 *
 *  Matched to fleet-control's OnlineWindowNs — the platform's one definition of "fresh
 *  enough to count as live" — so the badge and the dials answer the same question with
 *  the same threshold. A live box posts health about once a second, so this is generous. */
const TELEMETRY_FRESH_MS = 150_000;

/** A telemetry reading, but only if it is CURRENT — otherwise `null`, which the dials
 *  already render as the empty ring.
 *
 *  A dial is read as "right now", and every value in the record is last-known. Two ways
 *  that lies. A box switched off a month ago kept drawing its final CPU, memory and
 *  temperature — the temperature ring in alarm red, for powered-down hardware. And a box
 *  that is genuinely online can still be showing fossils: health reaches the cloud over
 *  the data-plane leaf, while `lastSeen` rides the enroll heartbeat over HTTPS, so a box
 *  with a dead leaf reports "Last seen: just now" beside a reading taken hours ago.
 *  Observed at two hours and four seconds respectively, on the same card.
 *
 *  Keying on the SAMPLE TIME covers both, because both are the same fact: this number is
 *  too old to be a reading. Deriving it from the box's status instead only caught the
 *  first — status is a proxy for freshness, and the proxy is exactly what was wrong. */
export function freshOnly(healthTsMs: number, value: number | null): number | null {
  if (!healthTsMs || Date.now() - healthTsMs > TELEMETRY_FRESH_MS) return null;
  return value;
}

// ── fleet-wide stats (all real — derived from status/org/revoked) ──
export interface FleetStats {
  total: number;
  online: number;
  offline: number;
  unknown: number;
  claimed: number;
  unclaimed: number;
  revoked: number;
}
export function recomputeStats(g: FleetGateway[]): FleetStats {
  return {
    total: g.length,
    online: g.filter((x) => x.status === 'online').length,
    offline: g.filter((x) => x.status === 'offline').length,
    unknown: g.filter((x) => x.status === 'unknown').length,
    claimed: g.filter((x) => x.orgId).length,
    unclaimed: g.filter((x) => !x.orgId).length,
    revoked: g.filter((x) => x.revoked).length,
  };
}

// ── geo — moved to base-ui (@vitesse/base-ui/map) so the fleet Map page and the
// dashboards map widget place boxes identically. Re-exported for existing callers.
export { geoToLatLng, regionOf, type GeoPoint, type Region } from '@vitesse/base-ui/map';
import { regionOf, type Region } from '@vitesse/base-ui/map';

/** Human org label for display/grouping — the NAME, never the raw id. Names only
 *  resolve for orgs the map was given; others fall back to 'Unknown org'. */
export function orgLabel(orgId: string, orgNames?: Map<string, string>): string {
  return orgId ? (orgNames?.get(orgId) ?? ft('group.unknownOrg')) : ft('status.unclaimed');
}

// ── grouping (the Status / Region / Channel / Organization pill tabs) ──
export type GroupMode = 'status' | 'region' | 'channel' | 'organization';
export interface FleetGroup {
  key: string;
  label: string;
  color?: BadgeColor;
  items: FleetGateway[];
}

export function groupFleet(
  gateways: FleetGateway[],
  mode: GroupMode,
  orgNames?: Map<string, string>,
): FleetGroup[] {
  if (mode === 'status') {
    const order: FleetGateway['status'][] = ['online', 'offline', 'unknown'];
    return order
      .map((s) => ({
        key: s,
        label: statusLabel(s),
        color: statusColor(s),
        items: gateways.filter((g) => g.status === s),
      }))
      .filter((grp) => grp.items.length);
  }
  if (mode === 'region') {
    const order: Region[] = ['Americas', 'EMEA', 'APAC', 'Unknown'];
    const by = new Map<Region, FleetGateway[]>();
    for (const g of gateways) {
      const r = regionOf(g.geo);
      (by.get(r) ?? by.set(r, []).get(r)!).push(g);
    }
    return order
      .filter((r) => by.has(r))
      .map((r) => ({ key: r, label: r, items: by.get(r)! }));
  }
  if (mode === 'organization') {
    const by = new Map<string, FleetGateway[]>();
    for (const g of gateways) {
      const k = g.orgId || '';
      (by.get(k) ?? by.set(k, []).get(k)!).push(g);
    }
    return [...by.entries()]
      .map(([orgId, items]) => ({ key: orgId || 'unclaimed', label: orgLabel(orgId, orgNames), items }))
      .sort((a, b) => b.items.length - a.items.length);
  }
  // channel — rollout channel isn't in the proto yet, so everything is the default
  // "Standard rollout" bucket. Populates once rollout data lands. (Placeholder.)
  return [{ key: 'standard', label: ft('group.standard'), items: [...gateways] }];
}

// ── time formatting ──
export function relativeTime(ms: number): string {
  if (!ms) return DASH;
  const diff = Date.now() - ms;
  if (diff < 0) return 'Just now';
  const s = Math.floor(diff / 1000);
  if (s < 45) return 'Just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

/** Host uptime (seconds) → compact "3d 4h" / "5h 20m" / "12m" / "45s". 0 → DASH. */
export function formatUptime(s: number): string {
  if (!s || s < 0) return DASH;
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${Math.floor(s)}s`;
}
